import { ElementRef, Injector, OnDestroy, Renderer2, Type, Directive, OnInit } from '@angular/core';

import { ApBaseComponent } from '../ap-base-component/index';

import {ApModalHelper} from './ApModalHelper';
import {ApModalHelperFactory} from './ApModalHelperFactory';

@Directive()
export abstract class ApBaseModalComponent<T, TData = unknown>
extends ApBaseComponent
implements OnInit, OnDestroy {
  private readonly modalHelper: ApModalHelper<T>;

  readonly modalData: TData;

  constructor(
    // For a component, this Injector is actually an instance of NodeInjector at runtime
    // which will have providers specific to this element or parents, and the providers in the root module Injector.
    // (https://github.com/angular/angular/blob/15.2.1/packages/core/src/render3/di.ts)
    elementInjector: Injector,
  ) {
    super();
    this.modalHelper = elementInjector.get(ApModalHelperFactory).create<T>(elementInjector, r => this.onHide(r));
    this.modalData = this.modalHelper.modalData<TData>();
    // Add a class here, so that flex styles could be applied to this element,
    // in case it needs to contain a scrollable container, which could require nested flex containers all the way down.
    // ex: making the .modal-body element scrollable
    // (theoretically this could be used for other situations as well, that don't have to do with scrolling)
    // eslint-disable-next-line max-len
    elementInjector.get<Renderer2>(Renderer2 as Type<Renderer2>).addClass(elementInjector.get(ElementRef).nativeElement, 'ap-modal-component-host');
  }

  ngOnInit(): void {
    this.modalHelper.onInit();
  }

  override ngOnDestroy(): void {
    this.modalHelper.ngOnDestroy();
    super.ngOnDestroy();
  }

  // close the activeModal because the user is done and there is a successful result
  closeModal(result: T | PromiseLike<T>): void {
    return this.modalHelper.emitAndClose(result);
  }

  // dismiss the activeModal (which rejects the promise)
  dismissModal(reason?: any): void {
    return this.modalHelper.dismiss(reason);
  }

  // resolve the promise because there is a successful result, but don't close the modal
  // call when closing of modal is already done elsewhere
  // At the moment, this should ONLY be used as a result of an event from DialogRef.closed observable
  protected emitWithoutClose(result: T | PromiseLike<T>): void {
    return this.modalHelper.emitWithoutClose(result);
  }

  protected onHide(reason: string): void {
    this.modalHelper.dismissWithoutClose(`Modal dismissed because of: '${reason}'`);
  }
}
